import React, { useState, useRef, useEffect } from 'react'
import { Link, router } from '@inertiajs/react'
import { toast } from 'sonner'
import { ArrowLeft, Printer, MessageCircle, Mail, Download, Wallet, Ban, Undo2, CreditCard, Pencil, Plus, Trash2, XCircle } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import BrandLogo from '../components/BrandLogo'
import SmartBack from '../components/SmartBack'
import PaymentModal from '../components/PaymentModal'
import { cn } from '../lib/utils'

const rand = (n) => `R${(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
// Local-midnight yyyy-mm-dd, and a 5-years-back floor — matches the server date guard.
const isoDay = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10)
const todayISO = () => isoDay(new Date())
const minISO = () => { const d = new Date(); d.setFullYear(d.getFullYear() - 5); return isoDay(d) }

// Inline tariff-code editor for an EXISTING line — retype the code (the same way it's typed in the
// "Add a line" box) and, on Enter or blur, resolve it against the SAME code catalogue to pull in the new
// treatment name + fee, KEEPING the tooth / quantity (no delete-and-re-add). An unknown code shows a
// clear inline error and leaves the line unchanged — it is never wiped. Disabled once the invoice has a
// payment banked (a code swap moves the fee, so it's a financial change). (Paul 2026-07-22.)
function CodeEdit({ line, codes, onResolve, listId, disabled = false }) {
  const [val, setVal] = useState(line.code || '')
  const [err, setErr] = useState(false)
  const lastActed = useRef(line.code || '')
  useEffect(() => { setVal(line.code || ''); setErr(false); lastActed.current = line.code || '' }, [ line.code ])
  const commit = () => {
    const typed = (val || '').trim()
    if (!typed) { setVal(line.code || ''); setErr(false); return }                       // don't wipe a line to nothing
    if (typed.toUpperCase() === String(lastActed.current || '').toUpperCase()) { setErr(false); return } // no change / already sent
    const pc = codes.find((c) => String(c.code).toUpperCase() === typed.toUpperCase())
    if (!pc) { setErr(true); return }                                                    // unknown → error, leave line as-is
    setErr(false); lastActed.current = pc.code
    onResolve(line, pc)
  }
  return (
    <div>
      <input
        list={listId}
        value={val}
        disabled={disabled}
        onChange={(e) => { setVal(e.target.value); setErr(false) }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit() }
          else if (e.key === 'Escape') { e.preventDefault(); setVal(line.code || ''); setErr(false); e.currentTarget.blur() }
        }}
        onBlur={commit}
        title={disabled ? 'Locked — invoice has a payment banked' : 'Type a tariff code and press Enter to swap it — keeps the tooth & quantity, and pulls in the new treatment name + fee'}
        className={`w-20 rounded border px-1.5 py-0.5 font-mono text-xs focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-brand-surface disabled:text-brand-muted ${err ? 'border-rose-400 ring-1 ring-rose-300' : 'border-brand-border focus:ring-brand-primary/40'}`}
      />
      {err && <p className="mt-0.5 text-[10px] font-medium text-rose-600">Unknown code</p>}
    </div>
  )
}

export default function InvoiceShow({ invoice = {}, practice = {}, patient = {}, procedure_codes = [], providers = [], place_of_service_options = [] }) {
  const [payOpen, setPayOpen] = useState(false)
  const editRef = useRef(null)
  const [editGlow, setEditGlow] = useState(false)
  // 🚨 Place of service (Paul 2026-08-27) — the patient claims off THIS invoice, so every line has
  // to say where the work happened: 11 rooms, 24 day clinic / day theatre, 21 admitted inpatient.
  // The invoice's prevailing POS is whatever its first line says; a new line inherits it so the one
  // code reception adds afterwards can't be the one line the medical aid rejects.
  const invoicePos = (invoice.lines || [])[0]?.place_of_service || '11'
  const mixedPos   = new Set((invoice.lines || []).map((l) => l.place_of_service || '11')).size > 1
  const [draft, setDraft] = useState({ code: '', description: '', tooth_number: '', quantity: '1', fee: '', icd10_code: '' })
  const jumpToEdit = () => {
    editRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setEditGlow(true); setTimeout(() => setEditGlow(false), 1600)
  }
  const balance = Number(invoice.balance || 0)
  const isPaid  = balance <= 0 && !invoice.void
  const credit  = Number(invoice.credit_available || 0)

  const applyCredit = () => {
    if (!invoice.account_id) return
    router.post(`/accounts/${invoice.account_id}/apply_credit`, { invoice_id: invoice.id }, { preserveScroll: true })
  }
  const writeOff = () => {
    const reason = window.prompt('Write this invoice off as bad debt? Enter a reason:')
    if (reason === null) return
    router.post(`/invoices/${invoice.id}/write_off`, { reason }, { preserveScroll: true })
  }
  // Cancel the invoice in full — removes it from the account / AR (status → void).
  const voidInvoice = () => {
    const reason = window.prompt('Cancel this invoice in full? It will be removed from the account entirely. Enter a reason:')
    if (reason === null) return
    router.post(`/invoices/${invoice.id}/void`, { reason }, { preserveScroll: true })
  }
  const reversePayment = (p) => {
    const reason = window.prompt(`Reverse this ${p.method?.toUpperCase()} payment of R${Number(p.amount).toFixed(2)}? Enter a reason:`)
    if (reason === null) return
    router.post(`/payments/${p.id}/reverse`, { reason }, { preserveScroll: true })
  }

  // Share this invoice with the patient (Paul 2026-08-04) — mirrors EstimateShow's send buttons.
  // Deliberate button press only, with a confirm — never automatic.
  const [sendingWa, setSendingWa] = useState(false)
  const sendWhatsapp = () => {
    if (!window.confirm(`Send this invoice to ${patient.name || 'the patient'} on WhatsApp, with the PDF attached?`)) return
    setSendingWa(true)
    router.post(`/invoices/${invoice.id}/send_whatsapp`, {}, {
      preserveScroll: true,
      onSuccess: (page) => { const al = page?.props?.flash?.alert; al ? toast.error(al) : toast.success(`Sent to ${patient.name || 'the patient'} on WhatsApp`) },
      onError:   (errs) => toast.error(Object.values(errs || {})[0] || 'Could not send'),
      onFinish:  () => setSendingWa(false),
    })
  }
  const [sendingEmail, setSendingEmail] = useState(false)
  const sendEmail = () => {
    if (!patient.email) { window.alert(`${patient.name || 'This patient'} has no email address on file — add one on their profile, or send on WhatsApp.`); return }
    if (!window.confirm(`Email this invoice (PDF) to ${patient.name || 'the patient'} at ${patient.email}?`)) return
    setSendingEmail(true)
    router.post(`/invoices/${invoice.id}/send_email`, {}, {
      preserveScroll: true,
      onSuccess: (page) => { const al = page?.props?.flash?.alert; al ? toast.error(al) : toast.success(`Emailed to ${patient.name || 'the patient'}`) },
      onError:   (errs) => toast.error(Object.values(errs || {})[0] || 'Could not email'),
      onFinish:  () => setSendingEmail(false),
    })
  }

  // Edit lines on an issued invoice. Tooth + ICD-10 are always editable (the patient needs them
  // for their claim); quantity + fee change the total, so they lock once any payment is banked —
  // the backend enforces the same rule. Sends the whole line with one field merged in.
  const hasPayments = Number(invoice.paid || 0) > 0
  const canEdit = invoice.writeable
  const patchLine = (l, changes, okMsg = 'Line updated') => {
    router.patch(`/invoices/${invoice.id}/lines/${l.id}`, {
      invoice_line: {
        tooth_number: l.tooth_number || '',
        quantity: l.quantity,
        fee: l.unit_fee,
        icd10_code: l.icd10_code || '',
        ...changes,
      },
    }, {
      preserveScroll: true,
      onSuccess: () => toast.success(okMsg),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not update line'),
    })
  }

  // Set the place of service on EVERY line in one go. A theatre case is one fact about the visit,
  // not twenty line edits — doing it per line is how one line gets missed. Not gated on payment:
  // POS is a claim field, not a money field, and a wrong POS is exactly what a patient comes back
  // to have fixed so their scheme will pay.
  const setAllPos = (code) => {
    router.patch(`/invoices/${invoice.id}/place-of-service`, { place_of_service: code }, {
      preserveScroll: true,
      onSuccess: () => toast.success('Place of service updated on every line'),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not set place of service'),
    })
  }

  // Inline CODE swap: the user retyped the tariff code on a line → resolve it to the new treatment +
  // fee (same catalogue the "Add a line" picker uses) and patch THIS line, preserving tooth/qty/ICD-10.
  // The server re-resolves + gates the change (blocked once a payment is banked), so this stays honest.
  const resolveCode = (l, pc) => {
    router.patch(`/invoices/${invoice.id}/lines/${l.id}`, {
      invoice_line: {
        procedure_code_id: pc.id,
        code: pc.code,
        description: pc.description,
        tooth_number: l.tooth_number || '',
        quantity: l.quantity,
        fee: pc.fee != null ? String(pc.fee) : '',
        icd10_code: l.icd10_code || '',
      },
    }, {
      preserveScroll: true,
      onSuccess: () => toast.success(`Code changed to ${pc.code}`),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not change code'),
    })
  }

  // Print = print the SAME server-rendered PDF as "Download PDF" (not the HTML page, which the
  // browser styles badly). Load the PDF in a hidden iframe and fire its print dialog so the
  // printout is byte-identical to the downloaded document. Falls back to opening the PDF if a
  // browser blocks programmatic print of an embedded PDF.
  const printPdf = () => {
    const url = `/invoices/${invoice.id}.pdf`
    let frame = document.getElementById('invoice-pdf-print-frame')
    if (!frame) {
      frame = document.createElement('iframe')
      frame.id = 'invoice-pdf-print-frame'
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
      document.body.appendChild(frame)
    }
    frame.onload = () => {
      try { frame.contentWindow.focus(); frame.contentWindow.print() }
      catch { window.open(url, '_blank', 'noopener') }
    }
    frame.src = url
  }

  // Add a missing line / remove a line. Backend blocks both once a payment is banked.
  const onPickCode = (code) => {
    const pc = (procedure_codes || []).find((c) => c.code === code)
    setDraft((d) => ({ ...d, code, description: pc?.description ?? d.description, fee: pc?.fee != null ? String(pc.fee) : d.fee }))
  }
  const addLine = () => {
    if (!draft.code.trim()) { toast.error('Pick or type a tariff code first'); return }
    router.post(`/invoices/${invoice.id}/lines`, { invoice_line: { ...draft, place_of_service: invoicePos } }, {
      preserveScroll: true,
      onSuccess: () => { toast.success('Line added'); setDraft({ code: '', description: '', tooth_number: '', quantity: '1', fee: '', icd10_code: '' }) },
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not add line'),
    })
  }
  // Estimate-style powers on the invoice (Paul 2026-06-26): describe the treatment → AI fills the
  // codes, and pick the treating dentist.
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiBusy, setAiBusy] = useState(false)
  const aiCompose = () => {
    if (!aiPrompt.trim()) { toast.error('Describe the treatment first'); return }
    setAiBusy(true)
    router.post(`/invoices/${invoice.id}/ai_compose`, { prompt: aiPrompt }, {
      preserveScroll: true,
      onSuccess: () => setAiPrompt(''),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'AI compose failed'),
      onFinish: () => setAiBusy(false),
    })
  }
  const setProvider = (name) => {
    router.patch(`/invoices/${invoice.id}/provider`, { provider_name: name }, {
      preserveScroll: true,
      onSuccess: () => toast.success('Provider updated'),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not set provider'),
    })
  }
  const setDate = (iso) => {
    if (!iso || iso === (invoice.date || '').slice(0, 10)) return
    router.patch(`/invoices/${invoice.id}/date`, { invoice_date: iso }, {
      preserveScroll: true,
      onSuccess: (page) => toast.success(page?.props?.flash?.notice || 'Invoice date updated'),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not update date'),
    })
  }
  const removeLine = (l) => {
    if (!window.confirm(`Remove ${l.code} — ${l.description}? This changes the invoice total.`)) return
    router.delete(`/invoices/${invoice.id}/lines/${l.id}`, {
      preserveScroll: true,
      onSuccess: () => toast.success('Line removed'),
      onError: (errs) => toast.error(Object.values(errs || {})[0] || 'Could not remove line'),
    })
  }

  return (
    <DashboardLayout>
      <div className="mb-4 flex items-center justify-between no-print">
        <SmartBack fallback={patient?.id ? `/patients/${patient.id}` : '/invoices'} />
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <button onClick={jumpToEdit} title="Jump to the line-item editor at the bottom of the page"
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100">
              <Pencil size={14} /> Edit line items
            </button>
          )}
          <button onClick={printPdf} title="Print the invoice (same format as Download PDF)" className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-sm text-brand-ink hover:bg-brand-surface">
            <Printer size={14} /> Print
          </button>
          <a
            href={`/invoices/${invoice.id}.pdf?dl=1`}
            download={`${invoice.number || invoice.invoice_number || 'invoice'}.pdf`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-sm text-brand-ink hover:bg-brand-surface"
          >
            <Download size={14} /> Download PDF
          </a>
          <button
            onClick={sendWhatsapp}
            disabled={sendingWa || !patient.whatsapp_phone}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-sm font-semibold text-emerald-700 shadow-sm hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            title={patient.whatsapp_phone ? 'Send this invoice to the patient on WhatsApp (PDF attached)' : 'No WhatsApp number on file for this patient — add one on their profile'}
          >
            <MessageCircle size={14} /> {sendingWa ? 'Sending…' : 'Send on WhatsApp'}
          </button>
          <button
            onClick={sendEmail}
            disabled={sendingEmail || !patient.email}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary bg-white px-3 py-1.5 text-sm font-semibold text-brand-primary shadow-sm hover:bg-brand-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            title={patient.email ? 'Email this invoice (PDF) to the patient' : 'No email address on file for this patient — add one on their profile'}
          >
            <Mail size={14} /> {sendingEmail ? 'Sending…' : 'Send email'}
          </button>
          <button
            onClick={() => setPayOpen(true)}
            disabled={invoice.void}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold shadow-sm',
              isPaid
                ? 'border border-brand-border bg-white text-brand-ink hover:bg-brand-surface'
                : 'bg-emerald-600 text-white hover:bg-emerald-700',
            )}
            title={invoice.void ? 'Void invoice — cannot record payment' : isPaid ? 'Add another payment or record a refund' : 'Record a card/cash/EFT payment'}
          >
            <Wallet size={14} /> {isPaid ? 'Add payment' : `Record payment · R${balance.toFixed(2)}`}
          </button>
          {credit > 0 && balance > 0 && invoice.writeable && (
            <button onClick={applyCredit} title={`Apply R${credit.toFixed(2)} account credit to this invoice`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100">
              <CreditCard size={14} /> Apply credit · R{credit.toFixed(2)}
            </button>
          )}
          {invoice.writeable && balance > 0 && (
            <button onClick={writeOff} title="Write off as bad debt"
              className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-sm text-brand-muted hover:bg-brand-surface">
              <Ban size={14} /> Write off
            </button>
          )}
          {invoice.writeable && (
            <button onClick={voidInvoice} title="Cancel this invoice in full — removes it from the account"
              className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-100">
              <XCircle size={14} /> Cancel invoice
            </button>
          )}
        </div>
      </div>

      <PaymentModal open={payOpen} onClose={() => setPayOpen(false)} invoice={invoice}
        onSaved={() => { try { if (patient?.id) localStorage.setItem('patient_stale:' + patient.id, '1') } catch (e) {} }} />

      {/* The compliant invoice document */}
      <div className="print-document mx-auto max-w-3xl rounded-xl border border-brand-border bg-white p-8 shadow-sm">
        {invoice.void && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-center text-sm font-semibold text-red-700">VOID</div>
        )}

        {/* Header — practice + practitioner identifiers (mandatory for the patient's self-claim) */}
        <div className="flex items-start justify-between border-b border-brand-border pb-5">
          <div>
            {/* Real logo if provided (public/brand/logo.png), else gold wordmark */}
            <BrandLogo className="h-14" />
            <p className="mt-2 text-xs text-brand-muted">{practice.address}</p>
            <p className="text-xs text-brand-muted">{practice.phone} · {practice.email}</p>
            <div className="mt-2 space-y-0.5 text-xs text-brand-ink">
              <p><span className="text-brand-muted">Practitioner:</span> {practice.practitioner}</p>
              <p><span className="text-brand-muted">HPCSA:</span> {practice.hpcsa} &nbsp; <span className="text-brand-muted">BHF practice no:</span> {practice.bhf}</p>
              <p><span className="text-brand-muted">Company reg:</span> {practice.company_reg}{practice.vat_registered && practice.vat_number ? ` · VAT ${practice.vat_number}` : ' · Not VAT-registered'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-brand-muted">Tax Invoice</p>
            <p className="font-mono text-sm font-semibold text-brand-ink">{invoice.number}</p>
            <p className="mt-1 text-xs text-brand-muted">Date: {fmtDate(invoice.date)}</p>
            {!isPaid && !invoice.void && invoice.date && (() => {
              const days = Math.floor((new Date(new Date().toDateString()) - new Date(invoice.date)) / 86400000)
              if (days <= 30) return null
              return <p className={`mt-1 inline-block rounded-md px-2 py-0.5 text-xs font-semibold ${days > 60 ? 'bg-brand-danger/10 text-brand-danger' : 'bg-amber-50 text-amber-700'}`}>{days} days overdue</p>
            })()}
          </div>
        </div>

        {/* Patient + scheme (claim-ready identifiers the scheme needs to accept the patient's claim) */}
        <div className="grid grid-cols-2 gap-4 border-b border-brand-border py-4 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-brand-muted">Patient</p>
            <p className="font-medium text-brand-ink">{patient.name}</p>
            <p className="text-brand-muted">{patient.phone}</p>
            {patient.date_of_birth && <p className="text-brand-muted">DOB: {patient.date_of_birth}</p>}
            {patient.id_number && <p className="text-brand-muted">ID: {patient.id_number}</p>}
            {invoice.provider_name && <p className="mt-1 text-brand-muted"><span className="text-xs uppercase tracking-wide">Provider:</span> {invoice.provider_name}</p>}
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-brand-muted">Medical aid (for your claim)</p>
            <p className="text-brand-ink">{patient.scheme || '— Private —'}</p>
            {patient.scheme_plan && <p className="text-brand-muted">Plan: {patient.scheme_plan}</p>}
            {patient.member_number && <p className="text-brand-muted">Member no: {patient.member_number}</p>}
            {patient.dependent_code && <p className="text-brand-muted">Dependent code: {patient.dependent_code}</p>}
            {patient.main_member && <p className="text-brand-muted">Main member: {patient.main_member}</p>}
            {patient.main_member_id && <p className="text-brand-muted">Main member ID: {patient.main_member_id}</p>}
          </div>
        </div>

        {/* Lines — code + tooth + Medical / Self / Amount per line (matches the practice format) */}
        <table className="mt-4 w-full text-sm">
          <thead className="border-b border-brand-border text-left text-xs uppercase tracking-wide text-brand-muted">
            <tr>
              <th className="py-2 font-semibold">Code</th>
              <th className="py-2 font-semibold">Description</th>
              <th className="py-2 text-center font-semibold" title="Place of service — where this item was performed (for the medical-aid claim)">POS</th>
              <th className="py-2 text-center font-semibold">Tooth</th>
              <th className="py-2 text-center font-semibold">Qty</th>
              <th className="py-2 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lines || []).map((l, i) => (
              <tr key={i} className={cn('border-b border-brand-border/50', l.baby_tooth_fee_wrong && 'bg-red-50')}>
                <td className="py-2 font-mono text-brand-ink">{l.code}</td>
                <td className="py-2 text-brand-ink">
                  {l.description}{l.vat_treatment === 'standard' ? ' (incl. 15% VAT)' : ''}
                  {l.icd10_code && <span className="ml-2 text-xs text-brand-muted">ICD-10: {l.icd10_code}</span>}
                  {l.baby_tooth_fee_wrong && <span className="ml-2 rounded bg-red-600 px-1 text-[10px] font-bold text-white" title="Baby-tooth extraction (code 8201 on a primary tooth) must be R250">⚠ SHOULD BE R250</span>}
                </td>
                <td className={cn('py-2 text-center font-mono', (l.place_of_service || '11') === '11' ? 'text-brand-muted' : 'font-semibold text-brand-ink')}
                    title={(place_of_service_options.find((o) => o.value === (l.place_of_service || '11')) || {}).label}>
                  {l.place_of_service || '11'}
                </td>
                <td className="py-2 text-center text-brand-muted">{l.tooth_number || '—'}</td>
                <td className="py-2 text-center text-brand-muted">{l.quantity}</td>
                <td className={cn('py-2 text-right', l.baby_tooth_fee_wrong ? 'font-bold text-red-600' : 'text-brand-ink')}>{rand(l.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="mt-4 ml-auto w-64 space-y-1 text-sm">
          <div className="flex justify-between text-brand-muted"><span>VAT (15%, incl.)</span><span>{rand(invoice.vat)}</span></div>
          <div className="flex justify-between border-t border-brand-border pt-1 font-semibold text-brand-ink"><span>Total</span><span>{rand(invoice.total)}</span></div>
          <div className="flex justify-between text-brand-muted"><span>Paid</span><span>{rand(invoice.paid)}</span></div>
          <div className={cn('flex justify-between font-semibold', invoice.balance > 0 ? 'text-brand-danger' : 'text-emerald-600')}><span>Balance due</span><span>{rand(invoice.balance)}</span></div>
        </div>

        <div className="mt-6 border-t border-brand-border pt-4 text-xs text-brand-muted">
          <p className="font-medium text-brand-ink">Payment / banking</p>
          <p>{practice.bank}</p>
          <p className="mt-2">This statement may be submitted to your medical aid for reimbursement. The practice does not claim on your behalf.</p>
        </div>

        {/* R1.5 — payments timeline (visible on-screen + in print) */}
        {(invoice.payments || []).length > 0 && (
          <div className="mt-6 border-t border-brand-border pt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-muted">Payments received</p>
            <table className="w-full text-sm">
              <tbody>
                {invoice.payments.map((p) => (
                  <tr key={p.id} className={cn('border-b border-brand-border/40 last:border-0', p.reversed && 'opacity-50')}>
                    <td className="py-1.5 text-brand-muted">{fmtDate(p.received_at)}</td>
                    <td className="py-1.5 capitalize text-brand-ink">{p.method}{p.kind === 'credit_applied' ? ' (credit)' : ''}{p.reversed ? ' · reversed' : ''}</td>
                    <td className="py-1.5 text-xs text-brand-muted">{p.reference || p.notes || ''}</td>
                    <td className={cn('py-1.5 text-right font-medium', p.reversed ? 'text-brand-muted line-through' : 'text-emerald-700')}>{rand(p.amount)}</td>
                    <td className="py-1.5 pl-3 text-right no-print">
                      <a href={`/payments/${p.id}/receipt`} target="_blank" rel="noreferrer" className="text-xs text-brand-primary hover:underline">Receipt</a>
                      {!p.reversed && ['payment', 'deposit', 'credit_applied'].includes(p.kind) && (
                        <button onClick={() => reversePayment(p)}
                          title={p.kind === 'credit_applied' ? 'Reverse — returns the credit to the account and reopens the invoice for editing' : 'Reverse this payment'}
                          className="ml-2 inline-flex items-center gap-0.5 text-xs text-brand-danger hover:underline">
                          <Undo2 size={11} /> Reverse{p.kind === 'credit_applied' ? ' credit' : ''}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit invoice lines (screen only — keeps the printed/PDF document clean) */}
      {canEdit && (
        <div ref={editRef} className={cn('no-print mx-auto mt-4 max-w-3xl rounded-xl border bg-white p-5 transition-shadow', editGlow ? 'border-amber-400 ring-2 ring-amber-300' : 'border-brand-border')}>
          <h3 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-brand-ink"><Pencil size={14} /> {(invoice.lines || []).length === 0 ? 'Add line items' : 'Edit line items'}</h3>
          {/* Shared code list so the inline Code editor autocompletes against the full catalogue. */}
          <datalist id="invoice-code-list">
            {(procedure_codes || []).map((c) => <option key={c.id} value={c.code}>{c.code} — {c.description}</option>)}
          </datalist>
          <p className="mb-3 text-[11px] text-brand-muted">
            {(invoice.lines || []).length === 0
              ? 'This invoice has no lines yet — add the tariff codes for the treatment below.'
              : (hasPayments ? 'Tooth number and ICD-10 can always be corrected.' : 'Retype a line’s Code and press Enter to swap it (keeps the tooth & qty). Tooth number and ICD-10 can always be corrected.')}
            {hasPayments
              ? ' This invoice has a payment banked, so quantity, fee, and adding/removing lines are locked — reverse the payment or raise a credit note to change them.'
              : ' Quantity and fee are editable, and you can add or remove lines, until a payment is banked. Totals recalculate automatically.'}
          </p>

          {/* Invoice date — a NON-financial header field, so it's correctable / back-datable even after a
              payment is banked (Paul 2026-07-07). Bounded to the last 5 years, no future-dating. */}
          <label className="mb-4 block text-xs font-medium text-brand-muted">Invoice date
            <input type="date" value={(invoice.date || '').slice(0, 10)} max={todayISO()} min={minISO()}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1 block w-full max-w-[220px] rounded-md border border-brand-border px-2 py-1.5 text-sm text-brand-ink" />
            <span className="mt-1 block text-[11px] text-brand-muted">Back-date to when the work was done — the document date updates immediately.</span>
          </label>

          {/* 🚨 Place of service — a CLAIM field, so (like the invoice date) it stays editable after a
              payment is banked. Set once for the whole visit; override a single line in the table below
              only for the rare plan that genuinely spans rooms and theatre. Paul 2026-08-27. */}
          <label className="mb-4 block text-xs font-medium text-brand-muted">Place of service — all lines
            <select value={mixedPos ? '' : invoicePos} onChange={(e) => { if (e.target.value) setAllPos(e.target.value) }}
              className="mt-1 block w-full max-w-[320px] rounded-md border border-brand-border px-2 py-1.5 text-sm text-brand-ink">
              {mixedPos && <option value="">— mixed across lines —</option>}
              {place_of_service_options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span className="mt-1 block text-[11px] text-brand-muted">
              Where the treatment was done. Sedation or GA in a hospital day theatre (home the same day) is
              <strong> 24</strong>; an admitted patient is <strong>21</strong>. It prints next to every line so the
              patient's medical aid pays it off the right benefit.
            </span>
          </label>

          {/* Provider picker + AI "describe the treatment" — the same powers as the estimate editor (Paul 2026-06-26) */}
          {!hasPayments && (
            <div className="mb-4 grid gap-3 rounded-lg border border-brand-border bg-brand-surface/40 p-3 md:grid-cols-2">
              <label className="text-xs font-medium text-brand-muted">Provider (treating dentist)
                <select value={invoice.provider_name || ''} onChange={(e) => setProvider(e.target.value)}
                  className="mt-1 w-full rounded-md border border-brand-border px-2 py-1.5 text-sm text-brand-ink">
                  <option value="">— select —</option>
                  {(providers || []).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <div className="text-xs font-medium text-brand-muted">Describe the treatment — AI fills the codes
                <div className="mt-1 flex gap-2">
                  <input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aiCompose() } }}
                    placeholder="e.g. cleaning, 2 fillings on 16 and 26, panoramic x-ray"
                    className="min-w-0 flex-1 rounded-md border border-brand-border px-2 py-1.5 text-sm" />
                  <button type="button" onClick={aiCompose} disabled={aiBusy}
                    className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-50">
                    {aiBusy ? 'Thinking…' : 'AI fill'}
                  </button>
                </div>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="border-b border-brand-border text-left text-[11px] uppercase tracking-wide text-brand-muted">
              <tr>
                <th className="py-1 font-semibold">Code</th>
                <th className="py-1 font-semibold">Description</th>
                <th className="py-1 text-center font-semibold" title="Place of service — where this item was performed (for the medical-aid claim)">POS</th>
                <th className="py-1 text-center font-semibold">Tooth</th>
                <th className="py-1 text-center font-semibold">Qty</th>
                <th className="py-1 text-right font-semibold">Fee</th>
                <th className="py-1 text-center font-semibold">ICD-10</th>
                <th className="py-1 text-right font-semibold">Amount</th>
                <th className="py-1 text-right font-semibold sr-only">Remove</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((l) => (
                <tr key={l.id} className="border-b border-brand-border/40 align-top">
                  <td className="py-1.5">
                    <CodeEdit line={l} codes={procedure_codes} onResolve={resolveCode} listId="invoice-code-list" disabled={hasPayments} />
                  </td>
                  <td className="py-1.5 text-brand-ink">{l.description}</td>
                  {/* Never disabled by hasPayments — POS is a claim field, not a money field. */}
                  <td className="py-1.5 text-center">
                    <select
                      value={l.place_of_service || '11'}
                      onChange={(e) => patchLine(l, { place_of_service: e.target.value }, 'Place of service updated')}
                      title="Place of service for this line (11 rooms · 24 day clinic / day theatre · 21 inpatient)"
                      className={cn('w-16 rounded border border-brand-border px-1 py-0.5 text-center text-xs',
                        (l.place_of_service || '11') !== '11' && 'font-semibold text-brand-ink')}
                    >
                      {place_of_service_options.map((o) => <option key={o.value} value={o.value} title={o.label}>{o.value}</option>)}
                    </select>
                  </td>
                  <td className="py-1.5 text-center">
                    <input
                      key={`tooth-${l.id}-${l.tooth_number || ''}`}
                      defaultValue={l.tooth_number || ''}
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (l.tooth_number || '')) patchLine(l, { tooth_number: v }, 'Tooth updated') }}
                      placeholder="—"
                      title="Tooth number (FDI notation, e.g. 11 / 36)"
                      className="w-14 rounded border border-brand-border px-1.5 py-0.5 text-center text-xs"
                    />
                  </td>
                  <td className="py-1.5 text-center">
                    <input
                      key={`qty-${l.id}-${l.quantity}`}
                      defaultValue={l.quantity}
                      inputMode="numeric"
                      disabled={hasPayments}
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== String(l.quantity)) patchLine(l, { quantity: v }, 'Quantity updated') }}
                      title={hasPayments ? 'Locked — invoice has a payment banked' : 'Quantity / units'}
                      className="w-12 rounded border border-brand-border px-1.5 py-0.5 text-center text-xs disabled:cursor-not-allowed disabled:bg-brand-surface disabled:text-brand-muted"
                    />
                  </td>
                  <td className="py-1.5 text-right">
                    <input
                      key={`fee-${l.id}-${l.unit_fee}`}
                      defaultValue={l.unit_fee}
                      inputMode="decimal"
                      disabled={hasPayments}
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== String(l.unit_fee)) patchLine(l, { fee: v }, 'Fee updated') }}
                      title={hasPayments ? 'Locked — invoice has a payment banked' : 'Fee per unit (incl. VAT)'}
                      className="w-20 rounded border border-brand-border px-1.5 py-0.5 text-right text-xs disabled:cursor-not-allowed disabled:bg-brand-surface disabled:text-brand-muted"
                    />
                  </td>
                  <td className="py-1.5 text-center">
                    <input
                      key={`icd-${l.id}-${l.icd10_code || ''}`}
                      defaultValue={l.icd10_code || ''}
                      onBlur={(e) => { const v = e.target.value.trim().toUpperCase(); if (v !== (l.icd10_code || '')) patchLine(l, { icd10_code: v }, 'ICD-10 updated') }}
                      placeholder="ICD-10"
                      title="ICD-10 diagnosis code for this line (for the patient's medical-aid claim)"
                      className="w-20 rounded border border-brand-border px-1.5 py-0.5 text-center text-xs uppercase"
                    />
                  </td>
                  <td className="py-1.5 text-right text-brand-ink">{rand(l.line_total)}</td>
                  <td className="py-1.5 pl-1 text-right">
                    <button
                      onClick={() => removeLine(l)}
                      disabled={hasPayments}
                      title={hasPayments ? 'Locked — invoice has a payment banked' : 'Remove this line'}
                      className="rounded p-1 text-rose-500 hover:bg-rose-50 disabled:cursor-not-allowed disabled:text-brand-muted disabled:hover:bg-transparent"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Add a missing line (e.g. a forgotten code or a duplicate to correct). Locked once paid. */}
          {!hasPayments && (
            <div className="mt-3 border-t border-brand-border pt-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-brand-ink"><Plus size={13} /> Add a line</div>
              <div className="flex flex-wrap items-end gap-2">
                <input list="invoice-codes" value={draft.code} onChange={(e) => onPickCode(e.target.value.trim())} placeholder="Code" className="w-24 rounded border border-brand-border px-2 py-1 text-xs" />
                <datalist id="invoice-codes">
                  {(procedure_codes || []).map((c) => <option key={c.id} value={c.code}>{c.code} — {c.description}</option>)}
                </datalist>
                <input value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} placeholder="Description" className="min-w-[160px] flex-1 rounded border border-brand-border px-2 py-1 text-xs" />
                <input value={draft.tooth_number} onChange={(e) => setDraft((d) => ({ ...d, tooth_number: e.target.value }))} placeholder="Tooth" className="w-16 rounded border border-brand-border px-2 py-1 text-center text-xs" />
                <input value={draft.quantity} onChange={(e) => setDraft((d) => ({ ...d, quantity: e.target.value }))} inputMode="numeric" placeholder="Qty" className="w-14 rounded border border-brand-border px-2 py-1 text-center text-xs" />
                <input value={draft.fee} onChange={(e) => setDraft((d) => ({ ...d, fee: e.target.value }))} inputMode="decimal" placeholder="Fee" className="w-24 rounded border border-brand-border px-2 py-1 text-right text-xs" />
                <input value={draft.icd10_code} onChange={(e) => setDraft((d) => ({ ...d, icd10_code: e.target.value.toUpperCase() }))} placeholder="ICD-10" className="w-24 rounded border border-brand-border px-2 py-1 text-center text-xs uppercase" />
                <button onClick={addLine} className="inline-flex items-center gap-1 rounded-lg bg-brand-ink px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"><Plus size={13} /> Add</button>
              </div>
              <p className="mt-1 text-[11px] text-brand-muted">Pick a tariff code (description &amp; fee auto-fill) or type your own. Leave fee blank to use the code's default.</p>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  )
}
